// PairingShell — UI compartilhada pelo gate de conta (AccountGateScreen) e pelas
// telas internas de reconfiguração (AccountLogin/AccountSignup). Renderiza os 6
// stages do hook `usePairingFlow` (loading/waiting/finalizing/error/
// needsPreference/awaitingSetup). Copy de título/subtítulo vem do caller pra cada
// tela ter sua própria voz.
//
// I5+I6 (gate de conta obrigatória): o stage 'waiting' ganhou uma apresentação
// RICA opcional — QR grande (tier-aware), instruções 1-2-3 ao lado e o código em
// destaque (caminho universal sem câmera) — ligada SÓ quando o caller passa as
// props extras (gate). As telas internas legadas não passam nada e seguem com o
// layout enxuto original.
//
// FIX 2026-07 (achados da LG real):
//  1. COPY HONESTA — o botão de escape rotulava sempre "Voltar"/"Cancelar", mas no
//     GATE ele SAI do app. Agora o rótulo vem do caller (`cancelLabel`): o gate
//     manda "Sair", as telas internas (onde o botão de fato volta) seguem com
//     "Voltar"/"Cancelar".
//  2. FOCO — os botões da tela de falha eram irmãos sem mapa explícito e a spatial
//     nav do Norigin prendia o foco no de saída ("não consigo mais selecionar o
//     Tentar de novo"). Agora o mapa é explícito e PURO (`pairing-focus.ts`).
//  3. ESCAPE SEMPRE VISÍVEL — os stages `loading` e `finalizing` não tinham NENHUM
//     elemento focável: se o pareamento travasse ali (rede lenta na TV), a tela
//     ficava sem rota de saída. Agora todo stage tem o botão de escape.

import { useTranslation } from '@app-iptv/i18n'
import { Button, setFocus } from '@app-iptv/ui'
import { useEffect, useState } from 'react'

import type { PairingStage } from '../hooks/usePairingFlow'
import styles from '../screens/AccountLoginScreen.module.css'

import {
  pairingFocusKey,
  resolvePairingFocus,
  type ArrowDir,
  type PairingAction,
  type PairingActionsLayout,
} from './pairing-focus'
import { QRDisplay } from './QRDisplay'
import { Spinner } from './Spinner'

interface PairingShellProps {
  stage: PairingStage
  onRetry: () => void
  onCancel: () => void
  /** Prefixo único de focusKey pra cada caller (login/signup/gate) — evita
   *  colisão se duas telas ficarem montadas em algum cenário hipotético. */
  focusPrefix: string
  /** Title/subtitle do stage 'waiting' (QR ativo). */
  waitingTitle: string
  waitingSubtitle: string
  /** Mensagem do stage 'needsPreference' — varia: login mostra "configure
   *  no site", signup mostra "conta criada, agora configure". */
  needsPreferenceMessage: string
  /** Rótulo do botão de escape (o que `onCancel` REALMENTE faz nesta tela).
   *  Default: "Voltar"/"Cancelar" (as telas internas voltam de fato). O GATE
   *  passa "Sair", porque lá o botão fecha o app. */
  cancelLabel?: string | undefined
  /** Ação secundária opcional, ao lado do escape (ex.: "Sair da conta" no gate de
   *  vínculo — a rota de escape de quem está preso num estado autenticado). */
  extraAction?: { label: string; onPress: () => void } | undefined
  /** Escape do stage 'awaitingSetup' (P2): configurar a fonte na própria TV em
   *  vez de esperar o celular. Só o gate passa. */
  onConfigureHere?: () => void
  // --- Apresentação rica do gate (opcional; só o AccountGateScreen passa) ---
  /** Tamanho do QR em px. Default do QRDisplay (360) se omitido. */
  qrSize?: number
  /** Passos 1-2-3 ao lado do QR (quem nunca pareou precisa do "como"). */
  instructions?: readonly string[]
  /** Texto "ou acesse <url> e use o código" (sem o código — o código sai do
   *  próprio stage). Quando presente, mostra a URL manual abaixo das instruções. */
  manualUrlLabel?: string
  /** URL absoluta do caminho sem câmera (ex: https://swipetvplayer.com/parear).
   *  Quando presente, vira um LINK tocável abaixo do texto manual — útil quando o
   *  gate é aberto num navegador touch (o próprio aparelho não escaneia o QR). Na
   *  TV o link é inofensivo (não recebe foco de controle, é só leitura). */
  manualUrlHref?: string
}

export function PairingShell({
  stage,
  onRetry,
  onCancel,
  focusPrefix,
  waitingTitle,
  waitingSubtitle,
  needsPreferenceMessage,
  cancelLabel,
  extraAction,
  onConfigureHere,
  qrSize,
  instructions,
  manualUrlLabel,
  manualUrlHref,
}: PairingShellProps) {
  const { t } = useTranslation()
  const [remainingSec, setRemainingSec] = useState<number>(0)

  const rich = instructions != null

  // Countdown TTL — só roda em stage 'waiting'
  useEffect(() => {
    if (stage.kind !== 'waiting') return
    const tick = () => {
      const sec = Math.max(0, Math.floor((stage.expiresAt - Date.now()) / 1000))
      setRemainingSec(sec)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [stage])

  // Quais botões existem NESTE stage (entrada do mapa de foco puro). O escape
  // (cancel) existe em TODOS os stages — é a rota de saída da tela.
  const layout: PairingActionsLayout = {
    retry: stage.kind === 'error' && stage.recoverable,
    cancel: true,
    extra: extraAction != null,
  }

  // Foco inicial — depende do stage. Em erro recuperável o foco cai no "Tentar de
  // novo" (ação primária); nos demais cai no escape, que é o único focável.
  useEffect(() => {
    if (stage.kind === 'error' && stage.recoverable) {
      setFocus(pairingFocusKey(focusPrefix, 'retry'))
    } else if (stage.kind === 'awaitingSetup' && onConfigureHere != null) {
      // TV-first: foco no único elemento navegável (o escape) pra o controle
      // remoto ter onde pousar enquanto a TV espera o celular.
      setFocus(`${focusPrefix}-configure-here`)
    } else {
      setFocus(pairingFocusKey(focusPrefix, 'cancel'))
    }
  }, [stage, focusPrefix, onConfigureHere])

  /** onArrowPress de um botão da linha de ações. Consome SEMPRE o evento
   *  (`return false`), com ou sem destino — mesma disciplina do ConfirmDialog.
   *
   *  Nas pontas da linha o mapa devolve `null` e antes disso devolvíamos `true`,
   *  entregando a seta pro spatial-nav do Norigin — o caminho que este mapa
   *  existe pra fechar (nó de tela irmã ainda registrado = foco pousando fora da
   *  tela, a armadilha reportada na LG real). A linha de ações é o ÚNICO grupo
   *  focável de todo stage do PairingShell, então não há destino legítimo fora
   *  dela: sem vizinho, o foco simplesmente FICA onde está. */
  const arrows = (from: PairingAction) => (dir: ArrowDir): boolean => {
    const to = resolvePairingFocus(from, dir, layout)
    if (to != null) setFocus(pairingFocusKey(focusPrefix, to))
    return false
  }

  const escapeLabel = cancelLabel ?? t('account.login.cancel')

  /** Linha de ações: [Tentar de novo] [Sair/Voltar] [ação extra]. Sempre tem ao
   *  menos o escape — nenhum stage fica sem saída focável. */
  const actionsRow = (label: string) => (
    <div className={styles.actions}>
      {layout.retry ? (
        <Button
          focusKey={pairingFocusKey(focusPrefix, 'retry')}
          onPress={onRetry}
          onArrowPress={arrows('retry')}
        >
          {t('account.login.tryAgain')}
        </Button>
      ) : null}
      <Button
        focusKey={pairingFocusKey(focusPrefix, 'cancel')}
        variant="ghost"
        onPress={onCancel}
        onArrowPress={arrows('cancel')}
      >
        {label}
      </Button>
      {extraAction != null ? (
        <Button
          focusKey={pairingFocusKey(focusPrefix, 'extra')}
          variant="ghost"
          onPress={extraAction.onPress}
          onArrowPress={arrows('extra')}
        >
          {extraAction.label}
        </Button>
      ) : null}
    </div>
  )

  if (stage.kind === 'loading') {
    return (
      <div className={styles.container}>
        <Spinner />
        <p className={styles.hint}>{t('account.login.initializing')}</p>
        {actionsRow(escapeLabel)}
      </div>
    )
  }

  if (stage.kind === 'finalizing') {
    return (
      <div className={styles.container}>
        <Spinner />
        <p className={styles.hint}>{t('account.login.finalizing')}</p>
        {actionsRow(escapeLabel)}
      </div>
    )
  }

  if (stage.kind === 'awaitingSetup') {
    // P2 — a TV espera o celular/site salvar a fonte. Spinner + copy clara +
    // escape "Configurar aqui na TV" (TV-first: focável por controle remoto).
    return (
      <div className={styles.container}>
        <Spinner />
        <h1 className={styles.title}>{t('gate.awaitingSetupTitle')}</h1>
        <p className={styles.subtitle}>{t('gate.awaitingSetupSubtitle')}</p>
        {onConfigureHere != null ? (
          // Par [Configurar aqui] [Sair] — mapa explícito próprio (estes botões não
          // são PairingActions). Mesma disciplina do `arrows` acima: a seta é SEMPRE
          // consumida, inclusive sem destino, pra não devolver o foco ao Norigin.
          <div className={styles.actions}>
            <Button
              focusKey={`${focusPrefix}-configure-here`}
              onPress={onConfigureHere}
              onArrowPress={(dir) => {
                if (dir === 'right' || dir === 'down') {
                  setFocus(pairingFocusKey(focusPrefix, 'cancel'))
                }
                return false
              }}
            >
              {t('gate.awaitingSetupConfigureHere')}
            </Button>
            <Button
              focusKey={pairingFocusKey(focusPrefix, 'cancel')}
              variant="ghost"
              onPress={onCancel}
              onArrowPress={(dir) => {
                if (dir === 'left' || dir === 'up') {
                  setFocus(`${focusPrefix}-configure-here`)
                }
                return false
              }}
            >
              {escapeLabel}
            </Button>
          </div>
        ) : (
          actionsRow(escapeLabel)
        )}
      </div>
    )
  }

  if (stage.kind === 'error') {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>{t('account.login.errorTitle')}</h1>
        <p className={styles.error}>{stage.message}</p>
        {actionsRow(escapeLabel)}
      </div>
    )
  }

  if (stage.kind === 'needsPreference') {
    // Tela informativa positiva — não erro. CTA de saída da tela.
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>{t('account.shared.needsPreferenceTitle')}</h1>
        <p className={styles.subtitle}>{needsPreferenceMessage}</p>
        {actionsRow(escapeLabel)}
      </div>
    )
  }

  // stage === 'waiting' — apresentação RICA (gate) quando há instruções/fallback.
  if (rich) {
    const expired = remainingSec <= 0
    return (
      <div className={`${styles.container} ${styles.gateContainer}`}>
        <h1 className={styles.title}>{waitingTitle}</h1>
        <p className={`${styles.subtitle} ${styles.gateSubtitle}`}>{waitingSubtitle}</p>

        <div className={styles.gateMain}>
          <div className={styles.qrBox}>
            <QRDisplay
              value={stage.qrPayload}
              {...(qrSize != null ? { size: qrSize } : {})}
              ariaLabel={t('common.qrPairingAria')}
            />
          </div>

          <div className={styles.gateSide}>
            {instructions != null && instructions.length > 0 ? (
              <ol className={styles.steps}>
                {instructions.map((step, i) => (
                  <li key={i} className={styles.step}>
                    <span className={styles.stepNum} aria-hidden="true">
                      {i + 1}
                    </span>
                    <span className={styles.stepText}>{step}</span>
                  </li>
                ))}
              </ol>
            ) : null}

            {/* Caminho UNIVERSAL (sem câmera): rótulo + código em destaque.
                Quem não tem celular com câmera (celular simples, 2º aparelho,
                PC) acessa o site em qualquer navegador e DIGITA este código —
                por isso o código ganha ênfase extra (.codeGate) no gate. */}
            <div className={styles.manualBlock}>
              {manualUrlLabel != null ? (
                <p className={styles.manualUrl}>{manualUrlLabel}</p>
              ) : null}

              {/* Link TOCÁVEL pro caminho sem câmera. Num navegador touch (o
                  próprio aparelho não escaneia o QR) abre o site direto; na TV é
                  só leitura (não recebe foco de controle). Mostrado só no gate
                  (quando o caller passa o href). */}
              {manualUrlHref != null ? (
                <a
                  className={styles.manualLink}
                  href={manualUrlHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={-1}
                >
                  {manualUrlHref.replace(/^https?:\/\//, '')}
                </a>
              ) : null}

              <div className={styles.codeBox}>
                {/* Rótulo "Código" redundante quando há texto manual (que já
                    termina em "...digite este código:"). Só aparece no layout
                    legado (sem manualUrlLabel). */}
                {manualUrlLabel == null ? (
                  <span className={styles.codeLabel}>{t('account.login.codeLabel')}</span>
                ) : null}
                <span className={`${styles.code} ${styles.codeGate}`}>{stage.code}</span>
              </div>
            </div>

            <p className={styles.countdown}>
              {!expired
                ? t('account.login.expiresIn', { time: formatTime(remainingSec) })
                : t('account.login.expired')}
            </p>
          </div>
        </div>

        {actionsRow(escapeLabel)}
      </div>
    )
  }

  // stage === 'waiting' — layout enxuto legado (telas internas /account/*).
  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{waitingTitle}</h1>
      <p className={styles.subtitle}>{waitingSubtitle}</p>

      <div className={styles.qrBox}>
        {/* Sem `size`: sai do token --qr-size (tier/canvas). Era `size={240}` —
            abaixo do piso de 360 do spec, no MESMO QR de pareamento
            (`qrPairingAria`) que o dono reclamou de pequeno no gate. */}
        <QRDisplay value={stage.qrPayload} ariaLabel={t('common.qrPairingAria')} />
      </div>

      <div className={styles.codeBox}>
        <span className={styles.codeLabel}>{t('account.login.codeLabel')}</span>
        <span className={styles.code}>{stage.code}</span>
      </div>

      <p className={styles.countdown}>
        {remainingSec > 0
          ? t('account.login.expiresIn', { time: formatTime(remainingSec) })
          : t('account.login.expired')}
      </p>

      {actionsRow(escapeLabel)}
    </div>
  )
}

function formatTime(totalSec: number): string {
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
